import { useState } from "react";
import { Mail, MessagesSquare, School, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/layout/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-ds-cream" data-testid="contact-page">
      <PageHero eyebrow="Contact us" title="We would love to hear from you." subtitle="Questions about the journey, a report or a payment, or would like a one-on-one session with a career counsellor? Write to us. We usually reply within one working day." />
      <section className="ds-section bg-ds-cream">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 space-y-6">
            <div className="rounded-[24px] bg-white border border-ds-plum/10 p-6">
              <Mail className="h-5 w-5 text-ds-plum" />
              <p className="mt-3 font-display text-lg font-semibold text-ds-plumdeep">Email</p>
              <a href="mailto:support@campussaarthi.com" className="text-sm text-ds-plum link-underline" data-testid="contact-email-link">support@campussaarthi.com</a>
            </div>
            <div className="rounded-[24px] bg-white border border-ds-plum/10 p-6">
              <MessagesSquare className="h-5 w-5 text-ds-plum" />
              <p className="mt-3 font-display text-lg font-semibold text-ds-plumdeep">Talk to a career counsellor</p>
              <p className="text-sm text-ds-inksoft">Already have your report? Write to us and we will arrange a one-on-one session with one of our career counsellors.</p>
            </div>
            <div className="rounded-[24px] bg-white border border-ds-plum/10 p-6">
              <School className="h-5 w-5 text-ds-plum" />
              <p className="mt-3 font-display text-lg font-semibold text-ds-plumdeep">Schools</p>
              <p className="text-sm text-ds-inksoft">Would you like to bring Disha to the students and families at your school? We would love to hear from you.</p>
            </div>
          </div>
          <div className="lg:col-span-8">
            {sent ? (
              <div className="rounded-[24px] bg-white border border-ds-plum/10 p-10 text-center" data-testid="contact-success">
                <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
                <h2 className="mt-4 font-display text-2xl font-semibold text-ds-plumdeep">Thank you, your message is on its way.</h2>
                <p className="mt-2 text-ds-inksoft">Our team will get back to you at the email you provided.</p>
                <button onClick={() => setSent(false)} className="mt-6 text-sm font-semibold text-ds-plum link-underline" data-testid="contact-send-another">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-[24px] bg-white border border-ds-plum/10 p-6 sm:p-8 grid sm:grid-cols-2 gap-5" data-testid="contact-form">
                <div className="space-y-2"><Label htmlFor="c-name">Your name</Label><Input id="c-name" required minLength={2} value={form.name} onChange={set("name")} className="h-12 bg-white border-ds-plum/20 text-[16px]" data-testid="contact-name-input" /></div>
                <div className="space-y-2"><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" required value={form.email} onChange={set("email")} className="h-12 bg-white border-ds-plum/20 text-[16px]" data-testid="contact-email-input" /></div>
                <div className="space-y-2 sm:col-span-2"><Label htmlFor="c-phone">Phone (optional)</Label><Input id="c-phone" value={form.phone} onChange={set("phone")} className="h-12 bg-white border-ds-plum/20 text-[16px]" data-testid="contact-phone-input" /></div>
                <div className="space-y-2 sm:col-span-2"><Label htmlFor="c-msg">How can we help?</Label><Textarea id="c-msg" required minLength={10} rows={6} value={form.message} onChange={set("message")} className="bg-white border-ds-plum/20 text-[16px]" data-testid="contact-message-input" /></div>
                <div className="sm:col-span-2 flex justify-end">
                  <button type="submit" disabled={busy} className="ds-btn bg-ds-plum text-white hover:bg-[#3d0232] disabled:opacity-60" data-testid="contact-submit-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send message
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
